// Which picture each answer option shows (TAS-527).
//
// The question step used to prefetch `metadata[option].image`, the remote
// questionicons file, and hold the room until it arrived. The screen rarely
// draws that file: a flavour with its own library icon, a taste-scale option
// and a grape variety all draw something else, and AnswerIconImage swaps most
// remote questionicons for a bundled cutout. So the curtain waited for pictures
// nobody saw, and then the screen waited again ("Loading...") for the ones it
// did draw. Both now ask this file, so the prefetch fetches what is drawn.
import { deriveIcons, exactFlavorImage, wineColourSwatch } from '../utils/flavorIcons';
import { tasteScaleImage } from '../utils/tasteScaleIcons';
import { questionIllustrationSource } from '../utils/questionIllustrations';

export const FALLBACK_OPTION_IMAGE =
  'https://cmrrsjwbbeurszecszhg.supabase.co/storage/v1/object/public/questionicons//Red%20wine.jpeg';

export function asksForColour(question) {
  return /colou?r/i.test(question?.type || '') || /colou?r/i.test(question?.text || '');
}

/** An option's metadata, matched on trimmed keys as the screen always has. */
export function optionMetaFor(metadata, option) {
  const all = metadata || {};
  const key = Object.keys(all).find((k) => k.trim() === String(option).trim());
  return key ? all[key] || {} : {};
}

/** The icons a grid card (GridOptionItem) is given. */
export function gridOptionIcons(option, meta, colour) {
  if (colour) return [wineColourSwatch(option) || meta.image || FALLBACK_OPTION_IMAGE];
  return deriveIcons(option, { ...meta, image: meta.image || FALLBACK_OPTION_IMAGE });
}

/**
 * The picture a list row (OptionItem) shows, or null when the option has none
 * of its own; the row then draws FALLBACK_OPTION_IMAGE.
 */
export function listOptionImage(option, meta, colour) {
  if (colour) return wineColourSwatch(option) || meta.image || null;
  return exactFlavorImage(option) || tasteScaleImage(option, meta.image) || meta.image || null;
}

// AnswerArtwork draws three icons and a "+N" when a card has more than four,
// and only draws strings it recognises as images (the rest are text).
const drawnIcons = (icons) => (icons.length > 4 ? icons.slice(0, 3) : icons);
const isImage = (icon) => typeof icon === 'string' && /^(https?:|data:|\/)/.test(icon);

/**
 * Every file the question screen will draw for this question's options, plus
 * its hint picture, as the URLs the <img> tags will request. Inline SVG
 * swatches need no download and are left out.
 * @param {{options?: string[], metadata?: object, type?: string, text?: string, hintImage?: string}} question
 * @param {{grid?: boolean}} [layout]  features.grid_layout
 * @returns {string[]}
 */
export function questionImageUrls(question, { grid = true } = {}) {
  if (!Array.isArray(question?.options)) return [];
  const colour = asksForColour(question);
  const urls = [];
  for (const option of question.options) {
    const meta = optionMetaFor(question.metadata, option);
    const icons = grid
      ? drawnIcons(gridOptionIcons(option, meta, colour))
      : [listOptionImage(option, meta, colour) || FALLBACK_OPTION_IMAGE];
    for (const icon of icons) {
      if (isImage(icon)) urls.push(questionIllustrationSource(icon));
    }
  }
  if (question.hintImage) urls.push(question.hintImage);
  return [...new Set(urls.filter((url) => url && !url.startsWith('data:')))];
}
